import { InputField } from '@components/utility/inputs';
import { Select } from '@components/utility/selects';
import { useAppSelector } from '@hooks/generic';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { CircularProgress, Grid2, InputAdornment } from '@mui/material';
import { QueryStatus } from '@reduxjs/toolkit/query';
import { ProofOfOwnershipAlert } from '@rla/components/alerts';
import { SERIAL_NUMBER_REGEX } from '@rla/constants/rla';
import { RLAFormFooter } from '@rla/routes/RLAForm/RLAFormFooter';
import { selectValidateSerialNumber, useGetVrmRequestTypesQuery } from '@rla/services';
import { RLAFormMode, VRMRecoveryForm } from '@rla/types';
import { useFormikContext } from 'formik';
import { useIntl } from 'react-intl';

export const RLAVRMForm = () => {
    const intl = useIntl();

    const formik = useFormikContext<VRMRecoveryForm>();
    const { values, touched, errors, handleChange, handleBlur } = formik;

    const { data: vrmFormRequestTypes, isLoading: isRequestTypesLoading } = useGetVrmRequestTypesQuery();

    const serialValidationState = useAppSelector((state) =>
        selectValidateSerialNumber({ serialNumber: formik.values.gx_serial_number })(state)
    );

    const serialNumberIsLoading = serialValidationState.status === QueryStatus.pending;
    const serialNumberValidationComplete =
        formik.touched.gx_serial_number && !serialNumberIsLoading && Boolean(formik.values.gx_serial_number);
    const serialNumberIsValid =
        serialNumberValidationComplete &&
        SERIAL_NUMBER_REGEX.test(formik.values.gx_serial_number) &&
        !formik.errors.gx_serial_number;

    return (
        <>
            <Grid2 size={{ xs: 12, md: 6 }}>
                <InputField
                    buffered
                    required
                    fullWidth
                    id="first_name"
                    name="first_name"
                    placeholder={intl.formatMessage({
                        id: 'common.firstName.placeholder',
                        defaultMessage: 'Enter first name',
                    })}
                    topLabel={intl.formatMessage({ id: 'common.firstName', defaultMessage: 'First Name' })}
                    debounceMs={0}
                />
            </Grid2>
            <Grid2 size={{ xs: 12, md: 6 }}>
                <InputField
                    buffered
                    required
                    fullWidth
                    id="last_name"
                    name="last_name"
                    placeholder={intl.formatMessage({
                        id: 'common.lastName.placeholder',
                        defaultMessage: 'Enter last name',
                    })}
                    topLabel={intl.formatMessage({ id: 'common.lastName', defaultMessage: 'Last Name' })}
                    debounceMs={0}
                />
            </Grid2>
            <Grid2 size={12}>
                <InputField
                    buffered
                    required
                    fullWidth
                    id="email"
                    name="email"
                    placeholder={intl.formatMessage({
                        id: 'common.email.placeholder',
                        defaultMessage: 'Enter email',
                    })}
                    topLabel={intl.formatMessage({ id: 'rla.vrmForm.email', defaultMessage: 'Email for VRM Access' })}
                    debounceMs={0}
                />
            </Grid2>
            <Grid2 size={12}>
                <ProofOfOwnershipAlert mode={RLAFormMode.VRM_ADMIN_ACCESS} />
            </Grid2>
            <Grid2 size={12}>
                <InputField
                    buffered
                    required
                    fullWidth
                    id="gx_serial_number"
                    name="gx_serial_number"
                    placeholder={intl.formatMessage({
                        id: 'common.serialNumber.placeholder',
                        defaultMessage: 'Enter serial number e.g HQ123456789',
                    })}
                    topLabel={intl.formatMessage({ id: 'rla.vrm.serialNumber', defaultMessage: 'GX Serial Number' })}
                    onChange={(event) => {
                        const isValidFormat = SERIAL_NUMBER_REGEX.test(event.target.value);

                        if (isValidFormat) {
                            formik.setFieldError('gx_serial_number', undefined);
                        }
                    }}
                    slotProps={{
                        input: {
                            endAdornment: serialNumberIsLoading ? (
                                <InputAdornment position="end">
                                    <CircularProgress size={20} />
                                </InputAdornment>
                            ) : serialNumberIsValid ? (
                                <InputAdornment position="end">
                                    <CheckCircleIcon color="success" fontSize="small" />
                                </InputAdornment>
                            ) : serialNumberValidationComplete ? (
                                <InputAdornment position="end">
                                    <CancelIcon color="error" fontSize="small" />
                                </InputAdornment>
                            ) : undefined,
                        },
                    }}
                    debounceMs={0}
                />
            </Grid2>
            <Grid2 size={12}>
                <InputField
                    buffered
                    id="vrm_portal_id"
                    name="vrm_portal_id"
                    required
                    placeholder={intl.formatMessage({
                        id: 'rla.vrm.vrmPortalId.placeholder',
                        defaultMessage: 'Enter VRM Portal ID',
                    })}
                    fullWidth
                    topLabel={intl.formatMessage({ id: 'rla.vrm.vrmPortalId', defaultMessage: 'VRM Portal ID' })}
                    debounceMs={0}
                />
            </Grid2>
            <Grid2 size={12}>
                <Select
                    fullWidth
                    virtualize
                    id="type_of_request"
                    name="type_of_request"
                    items={vrmFormRequestTypes ?? []}
                    type="radio"
                    placeholder={intl.formatMessage({
                        id: 'rla.vrm.requestType.placeholder',
                        defaultMessage: 'Choose a request type',
                    })}
                    size="smallest"
                    topLabel={intl.formatMessage({
                        id: 'rla.vrm.requestType',
                        defaultMessage: 'Request Type',
                    })}
                    value={values.type_of_request ?? ''}
                    error={touched.type_of_request && !!errors.type_of_request}
                    helperText={touched.type_of_request && errors.type_of_request ? errors.type_of_request : undefined}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    loading={isRequestTypesLoading}
                    loadingType="skeleton"
                />
            </Grid2>
            <RLAFormFooter mode={RLAFormMode.VRM_ADMIN_ACCESS} />
        </>
    );
};
